<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Tempo de Resposta por Agente',
  },
});

const sortedAgents = computed(() => {
  if (!props.data || props.data.length === 0) return [];
  return [...props.data]
    .filter(agent => agent.avg_first_response_minutes > 0)
    .sort(
      (a, b) => a.avg_first_response_minutes - b.avg_first_response_minutes
    );
});

const avgResponseTime = computed(() => {
  if (sortedAgents.value.length === 0) return 0;
  const total = sortedAgents.value.reduce(
    (sum, a) => sum + a.avg_first_response_minutes,
    0
  );
  return total / sortedAgents.value.length;
});

const maxTime = computed(() => {
  if (sortedAgents.value.length === 0) return 1;
  return Math.max(
    ...sortedAgents.value.map(a => a.avg_first_response_minutes),
    1
  );
});

function formatTime(minutes) {
  if (!minutes || minutes === 0) return '0 min';
  if (minutes < 1) return `${(minutes * 60).toFixed(0)}s`;
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)}h`;
}

function getTimeColor(minutes) {
  if (minutes <= 5) return 'text-n-teal-11';
  if (minutes <= 15) return 'text-n-blue-11';
  if (minutes <= 30) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}

function getBarColor(minutes) {
  if (minutes <= 5) return 'bg-n-teal-9';
  if (minutes <= 15) return 'bg-n-blue-9';
  if (minutes <= 30) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-timer text-lg text-n-blue-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-n-slate-11">Media:</span>
        <span class="text-lg font-bold" :class="getTimeColor(avgResponseTime)">
          {{ formatTime(avgResponseTime) }}
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="sortedAgents.length === 0"
      class="flex flex-col items-center justify-center h-64 text-n-slate-11"
    >
      <span class="i-lucide-clock text-4xl mb-2 opacity-50" />
      <span class="text-sm">Sem dados de tempo de resposta</span>
    </div>

    <!-- Agent List -->
    <div v-else class="flex flex-col gap-3 max-h-96 overflow-y-auto">
      <div
        v-for="(agent, index) in sortedAgents"
        :key="agent.agent_id"
        class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors"
      >
        <span
          class="text-sm font-medium w-6"
          :class="index < 3 ? 'text-n-teal-11' : 'text-n-slate-10'"
        >
          {{ index + 1 }}
        </span>

        <Avatar
          :name="agent.agent_name"
          :src="agent.thumbnail"
          :size="36"
          rounded-full
        />

        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between mb-1">
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ agent.agent_name }}
            </span>
            <div class="flex items-center gap-3">
              <span class="text-xs text-n-slate-10">
                P90: {{ formatTime(agent.p90_minutes) }}
              </span>
              <span
                class="text-lg font-bold"
                :class="getTimeColor(agent.avg_first_response_minutes)"
              >
                {{ formatTime(agent.avg_first_response_minutes) }}
              </span>
            </div>
          </div>

          <!-- Progress bar -->
          <div class="flex items-center gap-2">
            <div class="flex-1 h-2 bg-n-alpha-2 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-300"
                :class="getBarColor(agent.avg_first_response_minutes)"
                :style="{
                  width: `${Math.min((agent.avg_first_response_minutes / maxTime) * 100, 100)}%`,
                }"
              />
            </div>
          </div>

          <!-- Stats row -->
          <div class="flex items-center gap-4 mt-2 text-xs text-n-slate-10">
            <span>Mediana: {{ formatTime(agent.median_minutes) }}</span>
            <span>Min: {{ formatTime(agent.min_minutes) }}</span>
            <span>Max: {{ formatTime(agent.max_minutes) }}</span>
            <span class="ml-auto">{{ agent.total_responses || 0 }} respostas</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <div
      class="flex items-center justify-center gap-4 mt-4 pt-3 border-t border-n-weak text-xs"
    >
      <div class="flex items-center gap-1">
        <span class="w-2 h-2 rounded bg-n-teal-9" />
        <span class="text-n-slate-11">&lt;5min</span>
      </div>
      <div class="flex items-center gap-1">
        <span class="w-2 h-2 rounded bg-n-blue-9" />
        <span class="text-n-slate-11">&lt;15min</span>
      </div>
      <div class="flex items-center gap-1">
        <span class="w-2 h-2 rounded bg-n-amber-9" />
        <span class="text-n-slate-11">&lt;30min</span>
      </div>
      <div class="flex items-center gap-1">
        <span class="w-2 h-2 rounded bg-n-ruby-9" />
        <span class="text-n-slate-11">&gt;30min</span>
      </div>
    </div>
  </div>
</template>
